// Sentry must be imported before any other app code
import './lib/sentry';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import App from './App';
import { RemoteConfigProvider } from './context/RemoteConfigContext';
import '@shared/services/firebase';
import { initAnalytics } from '@shared/services/analyticsTracker';
import { dismissNativeKeyboard, initNativeKeyboardRecovery } from './utils/nativeKeyboard';
import { initOtaUpdates } from './lib/otaUpdates';
import '../index.css';

// Initialize GA4 + Fullstory + PostHog (fire-and-forget, non-blocking)
initAnalytics({
  fullstoryOrgId: import.meta.env.VITE_FULLSTORY_ORG_ID,
  fullstoryAssetMapId: import.meta.env.VITE_FULLSTORY_ASSET_MAP_ID,
  posthogKey: import.meta.env.VITE_POSTHOG_KEY,
  posthogHost: import.meta.env.VITE_POSTHOG_HOST,
  // VITE_APP_VERSION is not set by any build; the package.json version is
  // injected as __APP_VERSION__ (vite.config.ts `define`), so prefer it.
  appVersion: import.meta.env.VITE_APP_VERSION || __APP_VERSION__,
  // dev and prod share one PostHog project; this super property splits them.
  environment: import.meta.env.VITE_APP_ENV || 'development',
});

initNativeKeyboardRecovery();

// Mark this web bundle healthy for the OTA updater (rollback guard) and log
// which bundle is running. No-op on web and in updater-disabled binaries.
initOtaUpdates();

// iOS WKWebView blank screen recovery — when iOS suspends the WebView's JS context
// in the background, the app can resume to a blank screen. Detect this on foreground
// resume and reload the page to restore the UI.
if (typeof (window as { Capacitor?: unknown }).Capacitor !== 'undefined') {
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        // Android can leave the webview shrunk after backgrounding with IME state stale.
        void dismissNativeKeyboard();

        // Check if the root element has been emptied (blank screen).
        // A small delay lets the WebView settle before checking.
        setTimeout(() => {
          const root = document.getElementById('root');
          if (root && root.childElementCount === 0) {
            window.location.reload();
          }
        }, 300);
      }
    });
  }).catch(() => {});
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <RemoteConfigProvider>
        <App />
      </RemoteConfigProvider>
    </BrowserRouter>
  </React.StrictMode>
);
